import React, { PureComponent } from "react";
import styled, { keyframes, css } from "styled-components";
import NumberFormat from "react-number-format";
import Loader from "react-loaders";
import Fragment from "react-dot-fragment";
import { batchRequest, postRequest, putRequest } from "../../../api/ApiService";
import { incrementUnacceptedDecisions } from "../../../api/SubmitDecisions";
import { logError } from "../../../services/utils";
import EventEmitter from "../../../services/event-emitter";
import { changeLocation } from "../../../api/Location";
import FinanceStyled from "../../finance/style";
import WarningAlert from "../../alerts/warning";

const AVAILABLE_MACHINES_URL = `${process.env.REACT_APP_API_URL}static/machines/`;
const TEAM_MACHINES_URL = `${process.env.REACT_APP_API_URL}team/machines/`;
const PRODUCT_MANUFACTURES_URL = `${process.env.REACT_APP_API_URL}team/product_manufactures/`;

// Annual operating hours used to convert units_per_hour → annual capacity.
// Matches the figure used elsewhere on the Operations page (see Machine
// Utilization help copy).
const ANNUAL_HOURS = 8736;

const FONT = "'Mukta Vaani', Lato, sans-serif";

// Tween a numeric value from its previous to current prop over ~600ms
// using requestAnimationFrame + easeOutCubic. Mirrors the CountUp
// pattern from Choose Your Lender / Choose Your Equity Investor — used
// for Scheduled and Total Available so they animate after a successful
// production post.
class CountUp extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { display: Number(props.value) || 0 };
  }

  componentDidUpdate(prevProps) {
    const next = Number(this.props.value) || 0;
    const prev = Number(prevProps.value) || 0;
    if (next !== prev) {
      this.animateTo(this.state.display, next);
    }
  }

  componentWillUnmount() {
    this._cancelled = true;
  }

  animateTo(from, to) {
    if (this._raf) cancelAnimationFrame(this._raf);
    const duration = 600;
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const step = () => {
      if (this._cancelled) return;
      const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = from + (to - from) * eased;
      this.setState({ display: t === 1 ? to : value });
      if (t < 1) {
        this._raf = requestAnimationFrame(step);
      }
    };
    this._raf = requestAnimationFrame(step);
  }

  render() {
    const { format } = this.props;
    return <span>{format(this.state.display)}</span>;
  }
}

const formatInt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US');

const Page = styled.div`
  padding: 0 48px 48px;
  max-width: 1280px;
  margin: 0 auto;
  font-family: ${FONT};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitle = styled.h2`
  font-weight: 700;
  font-size: 26px;
  line-height: 32px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const HeaderSubtitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 4px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 4px 10px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const CapacityCard = styled.div`
  background: #F4F6FB;
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 24px;
`;

const CapacityCardTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

const CapacityCardHeading = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionLabel = styled.div`
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const CapacityCardSubtitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 4px;
`;

const CapacityNumbers = styled.div`
  text-align: right;
`;

const CapacityScheduled = styled.span`
  font-weight: 700;
  font-size: 30px;
  line-height: 36px;
  color: rgba(0, 27, 59, 0.94);
`;

const CapacityTotal = styled.span`
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.4);
  margin-left: 4px;
`;

const CapacityUtilization = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 2px;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: #DEE3EC;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 18px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #5C91F5;
  width: ${p => Math.min(100, Math.max(0, p.pct || 0))}%;
  transition: width 0.2s ease;
`;

const CapacityMiniRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 18px;
`;

const CapacityMiniCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 14px 18px;
`;

const CapacityMiniLabel = styled.div`
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(0, 27, 59, 0.94);
  color: #FFFFFF;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0;
  text-transform: none;
  width: 240px;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 100ms ease;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const InfoIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(0, 27, 59, 0.4);
  color: rgba(0, 27, 59, 0.6);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  cursor: help;

  &:hover ${InfoTooltip} {
    opacity: 1;
  }
`;

const CapacityMiniValue = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: rgba(0, 27, 59, 0.94);
  margin-top: 4px;
`;

const CapacityMiniSub = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
  margin-top: 2px;
`;

const ScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 14px;
  margin-left: 28px;
  width: 75%;
`;

const ScheduleTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const ScheduleHint = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
`;

const ProductCard = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 14px;
  margin-left: 28px;
  width: 75%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
`;

const ProductNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProductName = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const StatusBadgeWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.status-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.3px;

  ${p => p.variant === 'released' && `
    background: #E8F5EC;
    color: #2DA548;
    border: 1px solid #B7DEC2;
  `}
  ${p => p.variant === 'not-released' && `
    background: #FFF1DD;
    color: #C77700;
    border: 1px solid #F0CB91;
    cursor: help;
  `}
`;

const StatusTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: ${FONT};
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  padding: 8px 12px;
  border-radius: 6px;
  width: 240px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const ProductDemand = styled.div`
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);

  strong {
    color: #5C91F5;
    font-weight: 600;
  }
`;

const ProductBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 28px;
  align-items: start;
`;

const ColumnLabel = styled.div`
  font-weight: 600;
  font-size: 11px;
  line-height: 16px;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const MachineHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
`;

const MachineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Pulsing halo used when this product has no production yet and it's
// Round 1 — visually nudges the student toward the machine-line rows
// so they realize they need to pick one before hitting Produce.
const machineOptionPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(92, 145, 245, 0.55); }
  70%  { box-shadow: 0 0 0 12px rgba(92, 145, 245, 0); }
  100% { box-shadow: 0 0 0 0 rgba(92, 145, 245, 0); }
`;

const MachineOption = styled.label`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${p => p.selected ? '#EBF2FF' : '#F5F6F7'};
  border: 1px solid ${p => p.selected ? '#5C91F5' : 'transparent'};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${p => p.selected ? '#EBF2FF' : '#EEF1F5'};
  }

  ${p => p.attention && !p.selected && css`
    background: #EBF2FF;
    border-color: #5C91F5;
    animation: ${machineOptionPulse} 1.6s ease-out infinite;
  `}

  ${p => p.locked && `
    opacity: 0.5;
    cursor: not-allowed;
    &:hover { background: #F5F6F7; }
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Wrapper + tooltip for a machine option that has been locked out
// because the product is already committed to a different line this
// round. Tooltip renders on top-right corner of the row so it doesn't
// clash with the radio dot on the left.
const MachineOptionWrapper = styled.span`
  position: relative;
  display: block;

  &:hover > span.machine-locked-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const MachineLockedTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: ${FONT};
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 320px;
  text-align: center;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const MachineRadio = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${p => p.selected ? '#5C91F5' : 'rgba(0, 27, 59, 0.3)'};
  background: #fff;
  margin-right: 12px;
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.selected ? '#5C91F5' : 'transparent'};
  }
`;

const MachineLineText = styled.span`
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  flex: 1;
`;

const MachineCap = styled.span`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 27, 59, 0.6);
`;

const UnitsLabel = ColumnLabel;

const UnitsInputWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const UnitsInput = styled.input`
  width: 100%;
  padding: 14px 64px 14px 16px;
  border-radius: 8px;
  border: 1px solid #D5D9DD;
  font-family: ${FONT};
  font-weight: 600;
  font-size: 18px;
  color: rgba(0, 27, 59, 0.94);
  text-align: left;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #5C91F5;
  }
`;

const FieldError = styled.div`
  font-family: ${FONT};
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: #C62828;
  margin-top: -4px;
  margin-bottom: 10px;
`;

const FieldSuccess = styled.div`
  font-family: ${FONT};
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: #2DA548;
  margin-top: -4px;
  margin-bottom: 10px;
`;

const UnitsSuffix = styled.span`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-family: ${FONT};
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.6);
  pointer-events: none;
`;

const PostButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  border: none;
  background: #B6BAE5;
  color: #fff;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #9DA1D8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${p => p.ready && `
    background: #2A4169;
    &:hover:not(:disabled) {
      background: #36507e;
    }
  `}
`;

// Wraps the Produce Units button so we can show an explanatory hover
// tooltip while the button is disabled. The tooltip only renders when
// there is an unmet-condition message to show (i.e. not while an API
// call is in flight, which has its own "Producing…" label).
const PostButtonTooltipWrap = styled.div`
  position: relative;

  &:hover .produce-units-tooltip { visibility: visible; opacity: 1; }
`;

const PostButtonTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 240px;
  max-width: 320px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 8px;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.12);
  font-family: ${FONT};
  font-size: 16px;
  line-height: 20px;
  font-weight: 400;
  color: rgba(0, 27, 59, 0.85);
  text-align: center;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;
  visibility: hidden;
  opacity: 0;
  transition: opacity 120ms ease-out;
  z-index: 10;
  pointer-events: none;

  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #ffffff;
    border-right: 1px solid #D5D9DD;
    border-bottom: 1px solid #D5D9DD;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;

class ManageProductionPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      available_machines: [],
      team_machines: [],
      product_manufactures: [],
      // Per-product UI state, keyed by product_id
      selectedMachines: {},
      unitsInputs: {},
      posting: {},
      errors: {},
      successes: {},
      // Holds the product id whose Update was clicked when allocations
      // exist for it. Drives the Allocation Alert popup; null when closed.
      allocationAlertProductId: null,
      // Holds the product id whose Update was clicked when the chosen
      // machine differs from the already-saved one AND units_produced
      // on the existing row is > 0. Drives the Machine Switch confirmation
      // (BE deletes the old row and creates a new one on this change,
      // so warn the student that production on the current line will go).
      machineSwitchAlertProductId: null,
    };
    // Per-product timeout handles for auto-clearing success messages.
    this._successTimers = {};
    this.eventemitter = EventEmitter.getInstance();
  }

  goToCustomerAnalysis = e => {
    e.preventDefault();
    this.eventemitter.trigger("coaching-feedback-hide");
    changeLocation("/ceo/analysis/market");
  };

  showPopupLowProduction = (units) => {
    const formatted = Math.round(Number(units) || 0).toLocaleString("en-US");
    this.eventemitter.trigger("coaching-feedback", [
      "Inventory Management",
      <Fragment>
        Whoa! You're only producing {formatted} units? How many image sensors are customers demanding again? You should double check customer demand in the <a href="/ceo/analysis/market" onClick={this.goToCustomerAnalysis}>customer analysis</a> before finalizing production.
      </Fragment>,
      <Fragment>
        <b>Rule of thumb:</b> aim to produce at least what customers are expected to buy so you don't leave revenue on the table.
      </Fragment>,
    ]);
  };

  showPopupNotReleasedYet = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Not Released Yet",
      <Fragment>
        Whoa! This product won't be released from design this year and therefore you won't be able to produce units.
        <br /><br />
        For more details on this product's release date, please follow up with your Head of Product who created the product. S/he has the power to change the design if needed.
      </Fragment>,
    ]);
  };

  componentDidMount() {
    this._mounted = true;
    this.fetchData();
  }

  componentWillUnmount() {
    this._mounted = false;
    Object.keys(this._successTimers).forEach(id => clearTimeout(this._successTimers[id]));
  }

  fetchData = () => {
    batchRequest([
      AVAILABLE_MACHINES_URL,
      TEAM_MACHINES_URL,
      PRODUCT_MANUFACTURES_URL,
    ])
      .then(res => {
        if (!this._mounted) return;
        if (res[0].statusCode !== 200 || res[1].statusCode !== 200 || res[2].statusCode !== 200) {
          throw res;
        }
        const available_machines = res[0].body;
        const team_machines = (res[1].body || []).filter(m => !m.sold_in_round);
        const product_manufactures = res[2].body || [];

        // Pre-fill selections and units from any existing manufactures
        // so the UI reflects the current schedule on open.
        const selectedMachines = {};
        const unitsInputs = {};
        product_manufactures.forEach(pm => {
          selectedMachines[pm.product] = pm.machine;
          unitsInputs[pm.product] = String(pm.units_produced || 0);
        });

        // Convenience default: if the team owns exactly one machine
        // line, auto-select it for every product that doesn't already
        // have a machine assigned. Students with a single line only
        // have to enter units. Teams with >1 line still pick manually.
        if (team_machines.length === 1) {
          const soloMachineId = team_machines[0] && team_machines[0].id;
          if (soloMachineId != null) {
            const products = this.props.products || [];
            products.forEach(p => {
              const pid = p && p.product_id;
              if (pid != null && selectedMachines[pid] == null) {
                selectedMachines[pid] = soloMachineId;
              }
            });
          }
        }

        this.setState({
          loading: false,
          available_machines,
          team_machines,
          product_manufactures,
          selectedMachines,
          unitsInputs,
        });
      })
      .catch(err => {
        logError(err, "ManageProductionPage", "fetchData");
        if (this._mounted) {
          this.setState({ loading: false });
        }
      });
  };

  // Production Capacity per (product, team_machine) is now read directly
  // from the team-decisions `max_allowed_matrix` prop. Backend already
  // accounts for release status, shared-machine scheduling, etc.
  getMaxAllowed = (productId, teamMachineId) => {
    const { maxAllowedMatrix = [] } = this.props;
    const row = maxAllowedMatrix.find(r =>
      Number(r.product) === Number(productId)
      && Number(r.machine) === Number(teamMachineId)
    );
    return row ? Number(row.max_allowed) || 0 : 0;
  };

  // ---------- capacity calculations ----------

  getMachineCapacityUnits = (teamMachine) => {
    const { available_machines } = this.state;
    const spec = available_machines.find(m => Number(m.id) === Number(teamMachine.machine));
    if (!spec) return 0;
    return Math.round(Number(spec.units_per_hour) * ANNUAL_HOURS);
  };

  get totalCapacity() {
    const { team_machines } = this.state;
    return team_machines.reduce(
      (sum, tm) => sum + this.getMachineCapacityUnits(tm),
      0
    );
  }

  // Demand forecast for a single product. Pulled from the team-decisions
  // price_forecasts array (passed in as a prop). The team-decisions
  // response is already round-scoped, so we just match on product_id.
  demandForecastFor = (productId) => {
    const { priceForecasts = [] } = this.props;
    const entry = priceForecasts.find(pf => Number(pf.product) === Number(productId));
    return entry ? Number(entry.forecast) || 0 : 0;
  };

  get totalDemandForecast() {
    const { products = [] } = this.props;
    return products.reduce(
      (sum, p) => sum + this.demandForecastFor(p.product_id),
      0
    );
  }

  // Scheduled units = sum of inventory_balances[*].units_produced for the
  // round. Sourced from the team-decisions response (passed via prop), so
  // it refreshes automatically whenever the parent's reloadData runs —
  // including right after a successful POST/PUT here.
  get scheduledUnits() {
    const { inventoryBalances = [] } = this.props;
    return inventoryBalances.reduce(
      (sum, b) => sum + (Number(b.units_produced) || 0),
      0
    );
  }

  // ---------- per-product handlers ----------

  selectMachine = (productId, teamMachineId) => () => {
    this.setState({
      selectedMachines: {
        ...this.state.selectedMachines,
        [productId]: teamMachineId,
      },
      errors: { ...this.state.errors, [productId]: null },
      successes: { ...this.state.successes, [productId]: null },
    });
  };

  changeUnits = (productId) => (e) => {
    const raw = (e.target.value || "").replace(/[^0-9]/g, "");
    this.setState({
      unitsInputs: { ...this.state.unitsInputs, [productId]: raw },
      errors: { ...this.state.errors, [productId]: null },
      successes: { ...this.state.successes, [productId]: null },
    });
  };

  // Pulls a user-friendly message out of a failed POST/PUT response.
  // Specifically rephrases the {"units_produced":["Max allowed N"]}
  // shape into "Units exceed production capacity. Maximum allowed is
  // N units." Falls back through other common DRF error shapes.
  extractPostError = (err) => {
    const body = err && err.response && err.response.body;
    if (!body) return null;
    if (typeof body === 'string') return body;

    if (Array.isArray(body.units_produced) && body.units_produced.length > 0) {
      const raw = String(body.units_produced[0]);
      const match = raw.match(/Max allowed\s+(\d+)/i);
      if (match) {
        const max = Number(match[1]);
        return `Units exceed production capacity. Maximum allowed is ${max.toLocaleString('en-US')} units.`;
      }
      return raw;
    }
    if (body.detail) return String(body.detail);
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length > 0) {
      return String(body.non_field_errors[0]);
    }
    for (const key of Object.keys(body)) {
      const val = body[key];
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
        return `${key}: ${val[0]}`;
      }
      if (typeof val === 'string' && val.length > 0) {
        return `${key}: ${val}`;
      }
    }
    return null;
  };

  // Click handler. Two confirmation gates run before the actual
  // POST/PUT:
  //
  //   1. Machine-switch warning: the student has existing production
  //      on one machine (units_produced > 0) and is switching to a
  //      different machine. BE deletes the old row and creates a new
  //      one, so the units on the current line go away.
  //   2. Allocation warning: an existing production exists AND the
  //      product has distribution-center allocations this round — BE
  //      wipes those on any production change.
  //
  // Both gates are skipped for brand-new schedules (nothing to warn
  // about yet). The machine-switch gate runs first because it is the
  // more fundamental state change; the allocation warning fires on
  // the confirm callback.
  postProduct = (productId) => () => {
    const { product_manufactures, selectedMachines } = this.state;
    const existing = product_manufactures.find(pm => pm.product === productId);

    const selectedMachineId = selectedMachines[productId];
    const isMachineSwitch = !!(
      existing
      && selectedMachineId != null
      && Number(existing.machine) !== Number(selectedMachineId)
      && Number(existing.units_produced || 0) > 0
    );
    if (isMachineSwitch) {
      this.setState({ machineSwitchAlertProductId: productId });
      return;
    }

    this.runAllocationCheckOrPost(productId);
  };

  // Second gate factored out so the machine-switch confirmation can
  // fall through into it (switching machines may ALSO need to clear
  // allocations if any exist for this product).
  runAllocationCheckOrPost = (productId) => {
    const { product_manufactures } = this.state;
    const { inventoryAllocations = [] } = this.props;
    const existing = product_manufactures.find(pm => pm.product === productId);
    const hasAllocations = (inventoryAllocations || [])
      .some(a => Number(a.product) === Number(productId) && (Number(a.units) || 0) > 0);

    if (existing && hasAllocations) {
      this.setState({ allocationAlertProductId: productId });
      return;
    }

    this.doPostProduct(productId);
  };

  confirmAllocationAlert = () => {
    const productId = this.state.allocationAlertProductId;
    this.setState({ allocationAlertProductId: null });
    if (productId != null) this.doPostProduct(productId);
  };

  cancelAllocationAlert = () => {
    this.setState({ allocationAlertProductId: null });
  };

  confirmMachineSwitchAlert = () => {
    const productId = this.state.machineSwitchAlertProductId;
    this.setState({ machineSwitchAlertProductId: null });
    if (productId != null) this.runAllocationCheckOrPost(productId);
  };

  cancelMachineSwitchAlert = () => {
    this.setState({ machineSwitchAlertProductId: null });
  };

  doPostProduct = (productId) => {
    const { selectedMachines, unitsInputs, product_manufactures } = this.state;
    const machineId = selectedMachines[productId];
    const units = Number(unitsInputs[productId]) || 0;
    if (!machineId) return;

    const existing = product_manufactures.find(pm => pm.product === productId);
    // Brand-new schedules need at least 1 unit; updates can drop to 0.
    if (!existing && units <= 0) return;

    // Capture BEFORE the POST: is this the team's first production
    // decision in Round 2? Used to fire the DC coaching popup once,
    // right after the very first successful Round-2 production.
    //
    // Test the sum of `units_produced` across product_manufactures —
    // the backend may pre-seed a product_manufactures row per product
    // with units_produced=0, so "no production yet" means the sum is
    // still 0 (empty array also satisfies this via reduce init).
    const totalUnitsProduced = Array.isArray(product_manufactures)
      ? product_manufactures.reduce((sum, pm) => sum + (Number(pm.units_produced) || 0), 0)
      : 0;
    const wasFirstProductionInRound2 =
      Number(this.props.currentRound) === 2
      && totalUnitsProduced <= 0;

    // Snapshot the saved value so we can revert the input if the
    // request fails — leaves the user looking at the actual backend
    // state rather than the rejected number they tried to submit.
    const priorValue = existing ? String(existing.units_produced || 0) : "";

    this.setState({ posting: { ...this.state.posting, [productId]: true } });
    const request = existing
      ? putRequest(`${PRODUCT_MANUFACTURES_URL}${existing.id}/`).send({
          product: productId,
          machine: machineId,
          units_produced: units,
        })
      : postRequest(PRODUCT_MANUFACTURES_URL).send({
          product: productId,
          machine: machineId,
          units_produced: units,
        });

    request
      .then(res => {
        // Coaching popups (ported from produce-product.js): the backend
        // echoes units_produced and months_to_produce on the response.
        // Low production → "review demand"; months_to_produce === 0 →
        // "product isn't released yet."
        const body = (res && res.body) || {};
        const unitsProduced = Number(body.units_produced);
        const monthsToProduce = Number(body.months_to_produce);
        if (Number.isFinite(unitsProduced) && unitsProduced > 0 && unitsProduced <= 100000) {
          this.showPopupLowProduction(unitsProduced);
        } else if (monthsToProduce === 0) {
          this.showPopupNotReleasedYet();
        }

        if (!existing) incrementUnacceptedDecisions("operations");
        if (this.props.reloadData) this.props.reloadData();
        // Rebuild product_manufactures from the response body before the
        // refetch lands. On a machine switch the BE deletes the old row
        // and creates a new one with a different id — if a follow-up
        // PATCH fired against this.state.product_manufactures before
        // fetchData completed, it would PUT to the stale (deleted) id
        // and 404. Picking the id up from the response immediately
        // closes that window.
        const existingPMs = Array.isArray(this.state.product_manufactures)
          ? this.state.product_manufactures
          : [];
        const nextPMs = body && body.id != null && body.product != null
          ? [
              ...existingPMs.filter(pm => Number(pm.product) !== Number(body.product)),
              body,
            ]
          : existingPMs;
        const nextSelectedMachines = body && body.product != null && body.machine != null
          ? { ...this.state.selectedMachines, [body.product]: body.machine }
          : this.state.selectedMachines;
        this.fetchData();
        this.setState({
          posting: { ...this.state.posting, [productId]: false },
          errors: { ...this.state.errors, [productId]: null },
          successes: { ...this.state.successes, [productId]: 'Success' },
          product_manufactures: nextPMs,
          selectedMachines: nextSelectedMachines,
        });
        if (wasFirstProductionInRound2 && this.props.onFirstProductionInRound2) {
          this.props.onFirstProductionInRound2();
        }
        // Auto-clear the success indicator after a few seconds.
        if (this._successTimers[productId]) clearTimeout(this._successTimers[productId]);
        this._successTimers[productId] = setTimeout(() => {
          if (!this._mounted) return;
          this.setState({
            successes: { ...this.state.successes, [productId]: null },
          });
        }, 3500);
      })
      .catch(err => {
        logError(err, "ManageProductionPage", "postProduct");
        const message = this.extractPostError(err)
          || "Could not submit production. Please try again.";
        this.setState({
          posting: { ...this.state.posting, [productId]: false },
          errors: { ...this.state.errors, [productId]: message },
          // Revert input to the saved value so the user sees what's
          // actually persisted, not the rejected number.
          unitsInputs: { ...this.state.unitsInputs, [productId]: priorValue },
        });
      });
  };

  // ---------- render helpers ----------

  renderCapacityCard() {
    const { inventoryBalances = [] } = this.props;
    const total = this.totalCapacity;
    const scheduled = this.scheduledUnits;
    const available = inventoryBalances.reduce(
      (sum, b) => sum + (Number(b.available_for_sale) || 0),
      0
    );
    const utilization = total > 0 ? (scheduled / total) * 100 : 0;
    const totalDemand = this.totalDemandForecast;

    return (
      <CapacityCard>
        <SectionLabel>Plant Production Capacity</SectionLabel>
        <CapacityMiniRow>
          <CapacityMiniCard>
            <CapacityMiniLabel>Units Produced</CapacityMiniLabel>
            <CapacityMiniValue>
              <CountUp value={scheduled} format={formatInt} />
            </CapacityMiniValue>
            <CapacityMiniSub>this period</CapacityMiniSub>
          </CapacityMiniCard>
          <CapacityMiniCard>
            <CapacityMiniLabel>Total Available</CapacityMiniLabel>
            <CapacityMiniValue>
              <CountUp value={available} format={formatInt} />
            </CapacityMiniValue>
            <CapacityMiniSub>units for sale</CapacityMiniSub>
          </CapacityMiniCard>
          <CapacityMiniCard>
            <CapacityMiniLabel>
              <span>Machine Annual Capacity Utilized</span>
              <InfoIconWrapper>
                ?
                <InfoTooltip>
                  Reflects a full year of production. Products released later in the year have proportionally less production capacity available.
                </InfoTooltip>
              </InfoIconWrapper>
            </CapacityMiniLabel>
            <CapacityMiniValue>{utilization.toFixed(1)}%</CapacityMiniValue>
            <CapacityMiniSub>
              <NumberFormat value={scheduled} displayType="text" thousandSeparator="," />
              {" / "}
              <NumberFormat value={total} displayType="text" thousandSeparator="," />
            </CapacityMiniSub>
          </CapacityMiniCard>
          <CapacityMiniCard>
            <CapacityMiniLabel>Total Demand Forecast</CapacityMiniLabel>
            <CapacityMiniValue>
              <NumberFormat value={totalDemand} displayType="text" thousandSeparator="," />
            </CapacityMiniValue>
            <CapacityMiniSub>units across all products</CapacityMiniSub>
          </CapacityMiniCard>
        </CapacityMiniRow>
      </CapacityCard>
    );
  }

  renderProductCard = (product) => {
    const { team_machines, selectedMachines, unitsInputs, posting, errors, successes } = this.state;
    const productId = product.product_id;
    const selectedMachineId = selectedMachines[productId];
    const unitsValue = unitsInputs[productId] != null ? unitsInputs[productId] : "";
    const isPosting = !!posting[productId];
    const isUpdate = Number(product.units_produced) > 0;
    // Nudge the machine-line rows with a pulsing halo only in Round 1
    // when this product has no production yet. Same pattern as the
    // Manage Promotion product pill.
    const attention = Number(this.props.currentRound) === 1 && !isUpdate;
    // Update is allowed at 0 (lets users zero out an existing production
    // run). Post still requires > 0 — submitting a brand-new schedule of
    // 0 units doesn't carry any decision.
    const ready = !!selectedMachineId && (isUpdate
      ? Number(unitsValue) >= 0 && unitsValue !== ""
      : Number(unitsValue) > 0);
    const demandForecast = this.demandForecastFor(productId);
    const buttonLabel = isPosting
      ? (isUpdate ? "Updating…" : "Producing…")
      : (isUpdate ? "Update Production" : "Produce Units");

    // Game rule: a product can only be manufactured on one line per
    // round. Once a production decision has been committed
    // (units_produced > 0), lock the other machine rows so the team
    // can't split or switch without first zeroing out the current
    // schedule. The currently-committed machine stays clickable.
    const committedMachineId = isUpdate ? Number(selectedMachineId) : null;
    const committedMachine = committedMachineId != null
      ? team_machines.find(m => Number(m.id) === committedMachineId)
      : null;
    const committedLineNumber = committedMachine ? committedMachine.machine : null;

    // months_available_to_produce lives on the team-decisions top-level
    // `products` array (id/name/months_available_to_produce/round/...),
    // not on operations_decisions.products. Look it up by id.
    const { teamProducts = [] } = this.props;
    const teamProduct = teamProducts.find(p => Number(p.id) === Number(productId));
    const monthsAvailable = teamProduct ? Number(teamProduct.months_available_to_produce) || 0 : 0;
    const isReleased = monthsAvailable > 0;

    return (
      <ProductCard key={productId}>
        <ProductHeader>
          <ProductNameRow>
            <ProductName>{product.product_name}</ProductName>
            {isReleased ? (
              <StatusBadge variant="released">Released</StatusBadge>
            ) : (
              <StatusBadgeWrapper>
                <StatusBadge variant="not-released">Not Yet Released</StatusBadge>
                <StatusTooltip className="status-tooltip">
                  Check with your Head of Product or CEO about the release date with this product.
                </StatusTooltip>
              </StatusBadgeWrapper>
            )}
          </ProductNameRow>
          <ProductDemand>
            Demand forecast:{" "}
            <strong>
              {demandForecast > 0
                ? <NumberFormat value={demandForecast} displayType="text" thousandSeparator="," suffix=" units" />
                : "-"}
            </strong>
          </ProductDemand>
        </ProductHeader>
        <ProductBody>
          <div>
            <MachineHeaderRow>
              <ColumnLabel>Machine Line</ColumnLabel>
              <ColumnLabel>Production Capacity</ColumnLabel>
            </MachineHeaderRow>
            <MachineList>
              {team_machines.length === 0 && (
                <MachineCap>
                  No production machines available. Acquire a machine first.
                </MachineCap>
              )}
              {[...team_machines]
                .sort((a, b) => Number(a.machine) - Number(b.machine))
                .map(tm => {
                const cap = this.getMaxAllowed(productId, tm.id);
                const selected = Number(selectedMachineId) === Number(tm.id);
                const locked = committedMachineId != null
                  && Number(tm.id) !== committedMachineId;
                const option = (
                  <MachineOption
                    key={tm.id}
                    selected={selected}
                    attention={!locked && attention}
                    locked={locked}
                    onClick={locked ? undefined : this.selectMachine(productId, tm.id)}
                  >
                    <MachineRadio selected={selected} />
                    <MachineLineText>Production Line {tm.machine}</MachineLineText>
                    <MachineCap>
                      <NumberFormat value={cap} displayType="text" thousandSeparator="," /> units
                    </MachineCap>
                  </MachineOption>
                );
                if (!locked) return option;
                return (
                  <MachineOptionWrapper key={tm.id}>
                    {option}
                    <MachineLockedTooltip className="machine-locked-tooltip">
                      You're already producing this product on Production
                      Line {committedLineNumber}. Only one production line
                      per product per round. To switch, set Line {committedLineNumber}'s
                      units to 0 first.
                    </MachineLockedTooltip>
                  </MachineOptionWrapper>
                );
              })}
            </MachineList>
          </div>
          <div>
            <UnitsLabel>Units to Produce</UnitsLabel>
            <UnitsInputWrapper>
              <UnitsInput
                type="text"
                value={unitsValue ? Number(unitsValue).toLocaleString('en-US') : ""}
                onChange={this.changeUnits(productId)}
                placeholder="0"
              />
              <UnitsSuffix>units</UnitsSuffix>
            </UnitsInputWrapper>
            {errors[productId] && (
              <FieldError>{errors[productId]}</FieldError>
            )}
            {successes[productId] && !errors[productId] && (
              <FieldSuccess>{successes[productId]}</FieldSuccess>
            )}
            {(() => {
              const machineMissing = !selectedMachineId;
              const unitsMissing = isUpdate
                ? (unitsValue === "" || Number(unitsValue) < 0)
                : !(Number(unitsValue) > 0);
              const showTooltip = !isPosting && (machineMissing || unitsMissing);
              let tooltipMsg = null;
              if (showTooltip) {
                if (machineMissing && unitsMissing) {
                  tooltipMsg = isUpdate
                    ? "Select a production line and enter the number of units to produce."
                    : "Select a production line and enter a number greater than 0 in Units to Produce.";
                } else if (machineMissing) {
                  tooltipMsg = "Select a production line.";
                } else {
                  tooltipMsg = isUpdate
                    ? "Enter the number of units to produce."
                    : "Enter a number greater than 0 in Units to Produce.";
                }
              }
              return (
                <PostButtonTooltipWrap>
                  <PostButton
                    ready={ready}
                    disabled={!ready || isPosting}
                    onClick={this.postProduct(productId)}
                  >
                    {buttonLabel}
                  </PostButton>
                  {tooltipMsg && (
                    <PostButtonTooltip
                      className="produce-units-tooltip"
                      role="tooltip"
                    >
                      {tooltipMsg}
                    </PostButtonTooltip>
                  )}
                </PostButtonTooltipWrap>
              );
            })()}
          </div>
        </ProductBody>
      </ProductCard>
    );
  };

  render() {
    const { products = [], onClose } = this.props;
    const {
      loading,
      allocationAlertProductId,
      machineSwitchAlertProductId,
      team_machines,
      product_manufactures,
      selectedMachines,
    } = this.state;

    return (
      <FinanceStyled.LenderOverlay>
        <Page>
          <HeaderRow>
            <HeaderLeft>
              <HeaderTitle>Manage Production</HeaderTitle>
              <HeaderSubtitle>
                Plan how much of each product to manufacture this year and which machine line to run it on.
                Balance demand forecasts against available capacity to make the most cost-effective production decision.
              </HeaderSubtitle>
            </HeaderLeft>
            <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
          </HeaderRow>

          {loading && (
            <LoaderWrap>
              <Loader type="ball-scale-ripple" active />
            </LoaderWrap>
          )}

          {!loading && (
            <div>
              {this.renderCapacityCard()}
              <ScheduleHeader>
                <ScheduleTitle>
                  Manufacturing Schedule: {products.length} {products.length === 1 ? "Product" : "Products"}
                </ScheduleTitle>
                <ScheduleHint>Select machine line and set production quantity</ScheduleHint>
              </ScheduleHeader>
              {products.map(this.renderProductCard)}
            </div>
          )}
        </Page>
        {allocationAlertProductId != null && (() => {
          const alertProduct = products.find(p => Number(p.product_id) === Number(allocationAlertProductId));
          const productName = alertProduct ? alertProduct.product_name : 'this product';
          return (
            <WarningAlert
              title="Allocation Alert"
              successButtonText="Yes, Update Production"
              onSuccess={this.confirmAllocationAlert}
              onCancel={this.cancelAllocationAlert}
              message={(
                <span>
                  Updating production for <strong>{productName}</strong> will remove any distribution center allocations you've made this round for this product.
                  <br /><br />
                  You'll need to redo those allocations after this change.
                  <br /><br />
                  Do you want to continue?
                </span>
              )}
            />
          );
        })()}
        {machineSwitchAlertProductId != null && (() => {
          const alertProduct = products.find(p => Number(p.product_id) === Number(machineSwitchAlertProductId));
          const productName = alertProduct ? alertProduct.product_name : 'this product';
          const existing = (product_manufactures || []).find(pm => pm.product === machineSwitchAlertProductId);
          const currentMachineId = existing ? Number(existing.machine) : null;
          const nextMachineId = Number((selectedMachines || {})[machineSwitchAlertProductId]);
          const currentLine = (team_machines || []).find(m => Number(m.id) === currentMachineId);
          const nextLine = (team_machines || []).find(m => Number(m.id) === nextMachineId);
          const currentLineLabel = currentLine ? `Production Line ${currentLine.machine}` : 'the current production line';
          const nextLineLabel = nextLine ? `Production Line ${nextLine.machine}` : 'the selected production line';
          return (
            <WarningAlert
              title="Switch Production Line?"
              successButtonText="Yes, Switch Production Line"
              onSuccess={this.confirmMachineSwitchAlert}
              onCancel={this.cancelMachineSwitchAlert}
              message={(
                <span>
                  <strong>{productName}</strong> is already being produced on <strong>{currentLineLabel}</strong>. Switching to <strong>{nextLineLabel}</strong> will cancel any production on <strong>{currentLineLabel}</strong>.
                  <br /><br />
                  A product cannot be produced on multiple production lines.
                  <br /><br />
                  Do you want to continue?
                </span>
              )}
            />
          );
        })()}
      </FinanceStyled.LenderOverlay>
    );
  }
}

export default ManageProductionPage;



// WEBPACK FOOTER //
// ./src/components/operations/decisions/manage-production-page.js